import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

export const opencodeToolIconKeyByName = {
  apply_patch: "file",
  bash: "terminal",
  edit: "file",
  execute: "terminal",
  glob: "search",
  grep: "search",
  multiedit: "file",
  patch: "file",
  question: "help",
  read: "file",
  skill: "skill",
  task: "bot",
  todowrite: "list",
  webfetch: "link",
  write: "file",
} as const;

export const opencodeMessageRoleSchema = z
  .enum(["user", "assistant"])
  .describe("OpencodeMessageRole");

export const opencodeMessageFeedbackSchema = z
  .enum(["positive", "negative", "none"])
  .describe("OpencodeMessageFeedback");

export const opencodeSessionSchema = z
  .object({
    id: z.uuid(),
    projectId: z.uuid(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
    opencodeId: z.string(),
    title: z.string().nullable(),
    rawSession: z.unknown().optional(),
  })
  .describe("OpencodeSession");

export const opencodeCommitFileChangeSchema = z
  .object({
    file: z.string(),
    status: z.enum(["added", "modified", "deleted", "renamed", "copied"]),
  })
  .describe("OpencodeCommitFileChange");

export const opencodeCommitSchema = z
  .object({
    sha: z.string(),
    shortSha: z.string(),
    message: z.string().nullable(),
    author: z.string().nullable(),
    authorEmail: z.string().nullable(),
    timestamp: z.string().nullable(),
    relativeTime: z.string().nullable(),
    // (Noah, 2026-07-08, REPL-29014): template name for the "Started from X" card; non-null only on the inherited template-origin commit.
    templateName: z.string().nullable(),
    tags: z.array(z.string()),
    files: z.array(opencodeCommitFileChangeSchema),
  })
  .describe("OpencodeCommit");

export const opencodeCommitDiffSchema = z
  .object({
    files: z.array(opencodeCommitFileChangeSchema),
  })
  .describe("OpencodeCommitDiff");

export const lastPublishedCommitShaSchema = z
  .object({
    sha: z.string(),
    hasBeenPublished: z.boolean(),
    publishedAt: z.string().nullish(),
  })
  .describe("LastPublishedCommitSha");

export const versionHistoryScopeSchema = z
  .enum(["all", "userApp"])
  .describe("VersionHistoryScope");

export const opencodePermissionRequestSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    permission: z.string(),
    patterns: z.array(z.string()),
    metadata: z.record(z.string(), z.unknown()),
    always: z.array(z.string()),
    tool: z
      .object({
        messageID: z.string(),
        callID: z.string(),
      })
      .optional(),
  })
  .passthrough()
  .describe("OpencodePermissionRequest");

const opencodeMetadataSchema = z.record(z.string(), z.unknown());

export const opencodeToolPartPermissionSchema = z
  .object({
    request: opencodePermissionRequestSchema,
    // A code-mode `execute` runs many MCP calls under one tool part and opencode
    // raises one permission per call, so a part can be waiting on several at
    // once. `request` is the one the prompt renders; the rest queue here so a
    // single reply resolves every call the part is blocked on.
    siblingRequests: z.array(opencodePermissionRequestSchema).optional(),
    requestedAt: z.iso.datetime(),
    resolvedAt: z.iso.datetime().nullable(),
  })
  .describe("OpencodeToolPartPermission");

export const opencodeToolPartMetadataSchema = z
  .object({
    permission: opencodeToolPartPermissionSchema.optional(),
  })
  .catchall(z.unknown())
  .describe("OpencodeToolPartMetadata");

export const opencodeMessageSchema = z
  .object({
    id: z.uuid(),
    opencodeSessionUuid: z.uuid(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
    opencodeId: z.string().nullable(),
    opencodeSessionId: z.string(),
    opencodeParentId: z.string().nullable(),
    completedAt: z.date().nullable(),
    role: opencodeMessageRoleSchema,
    feedback: opencodeMessageFeedbackSchema,
    finishReason: z.string().nullable(),
    error: z.unknown().nullable(),
    cost: z.number().nullable(),
    tokensInput: z.number().nullable(),
    tokensOutput: z.number().nullable(),
    tokensReasoning: z.number().nullable(),
    tokensCacheRead: z.number().nullable(),
    tokensCacheWrite: z.number().nullable(),
    model: z.string().nullable(),
    rawMessage: z.unknown().optional(),
  })
  .describe("OpencodeMessage");

const opencodeFilePartSourceTextSchema = z.object({
  value: z.string(),
  start: z.number(),
  end: z.number(),
});

const opencodeRangeSchema = z.object({
  start: z.object({
    line: z.number(),
    character: z.number(),
  }),
  end: z.object({
    line: z.number(),
    character: z.number(),
  }),
});

const opencodeFilePartSourceSchema = z.discriminatedUnion("type", [
  z.object({
    text: opencodeFilePartSourceTextSchema,
    type: z.literal("file"),
    path: z.string(),
  }),
  z.object({
    text: opencodeFilePartSourceTextSchema,
    type: z.literal("symbol"),
    path: z.string(),
    range: opencodeRangeSchema,
    name: z.string(),
    kind: z.number(),
  }),
]);

export const opencodeFilePartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("file"),
    mime: z.string(),
    filename: z.string().optional(),
    url: z.string(),
    source: opencodeFilePartSourceSchema.optional(),
    synthetic: z.boolean().optional(),
    metadata: opencodeMetadataSchema.optional(),
  })
  .passthrough()
  .describe("OpencodeFilePart");

export const opencodeToolStateSchema = z
  .discriminatedUnion("status", [
    z.object({
      status: z.literal("pending"),
      input: opencodeMetadataSchema,
      raw: z.string(),
    }),
    z.object({
      status: z.literal("running"),
      input: opencodeMetadataSchema,
      title: z.string().optional(),
      metadata: opencodeMetadataSchema.optional(),
      time: z.object({
        start: z.number(),
      }),
    }),
    z.object({
      status: z.literal("completed"),
      input: opencodeMetadataSchema,
      output: z.string(),
      title: z.string(),
      metadata: opencodeMetadataSchema,
      time: z.object({
        start: z.number(),
        end: z.number(),
        compacted: z.number().optional(),
      }),
      attachments: z.array(opencodeFilePartSchema).optional(),
    }),
    z.object({
      status: z.literal("error"),
      input: opencodeMetadataSchema,
      error: z.string(),
      metadata: opencodeMetadataSchema.optional(),
      time: z.object({
        start: z.number(),
        end: z.number(),
      }),
    }),
  ])
  .describe("OpencodeToolState");

const opencodeApiErrorSchema = z.object({
  name: z.literal("APIError"),
  data: z.object({
    message: z.string(),
    statusCode: z.number().optional(),
    isRetryable: z.boolean(),
    responseHeaders: z.record(z.string(), z.string()).optional(),
    responseBody: z.string().optional(),
  }),
});

export const opencodeTextPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("text"),
    text: z.string(),
    synthetic: z.boolean().optional(),
    ignored: z.boolean().optional(),
    time: z
      .object({
        start: z.number(),
        end: z.number().optional(),
      })
      .optional(),
    metadata: opencodeMetadataSchema.optional(),
  })
  .passthrough()
  .describe("OpencodeTextPart");

export const opencodeSubtaskPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("subtask"),
    prompt: z.string(),
    description: z.string(),
    agent: z.string(),
  })
  .passthrough()
  .describe("OpencodeSubtaskPart");

export const opencodeReasoningPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("reasoning"),
    text: z.string(),
    metadata: opencodeMetadataSchema.optional(),
    time: z.object({
      start: z.number(),
      end: z.number().optional(),
    }),
  })
  .passthrough()
  .describe("OpencodeReasoningPart");

export const opencodeToolPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("tool"),
    callID: z.string(),
    tool: z.string(),
    state: opencodeToolStateSchema,
    metadata: opencodeToolPartMetadataSchema.optional(),
  })
  .passthrough()
  .describe("OpencodeToolPart");

export interface WriteToolCall {
  path: string;
  content: string;
  callID: string;
  partID: string;
  status: "pending" | "running" | "completed" | "error";
}

export interface UploadAssetsToolCall {
  tool: "upload-assets";
  brandName: string | null;
  callID: string;
  partID: string;
  status: "pending" | "running" | "completed" | "error";
}

export function getWriteToolCallFromPart(part: object): WriteToolCall | null {
  const parsed = opencodeToolPartSchema.safeParse(part);
  if (!parsed.success) {
    return null;
  }
  if (parsed.data.tool.toLowerCase() !== "write") {
    return null;
  }
  const input = parsed.data.state.input;
  const rawPath = input.filePath ?? input.path ?? input.file_path;
  if (typeof rawPath !== "string" || rawPath.length === 0) {
    return null;
  }
  const content = typeof input.content === "string" ? input.content : "";
  return {
    path: rawPath,
    content,
    callID: parsed.data.callID,
    partID: parsed.data.id,
    status: parsed.data.state.status,
  };
}

export function getUploadAssetsCallFromPart(
  part: object,
): UploadAssetsToolCall | null {
  const parsed = opencodeToolPartSchema.safeParse(part);
  if (!parsed.success) {
    return null;
  }
  if (parsed.data.tool.toLowerCase() !== "upload-assets") {
    return null;
  }
  const input = parsed.data.state.input;
  const brandName =
    typeof input.brandName === "string" ? input.brandName : null;
  return {
    tool: "upload-assets",
    brandName,
    callID: parsed.data.callID,
    partID: parsed.data.id,
    status: parsed.data.state.status,
  };
}

export const opencodeStepStartPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("step-start"),
    snapshot: z.string().optional(),
  })
  .passthrough()
  .describe("OpencodeStepStartPart");

export const opencodeStepFinishPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("step-finish"),
    reason: z.string(),
    snapshot: z.string().optional(),
    cost: z.number(),
    tokens: z.object({
      input: z.number(),
      output: z.number(),
      reasoning: z.number(),
      cache: z.object({
        read: z.number(),
        write: z.number(),
      }),
    }),
  })
  .passthrough()
  .describe("OpencodeStepFinishPart");

export const opencodeSnapshotPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("snapshot"),
    snapshot: z.string(),
  })
  .passthrough()
  .describe("OpencodeSnapshotPart");

export const opencodePatchPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("patch"),
    hash: z.string(),
    files: z.array(z.string()),
  })
  .passthrough()
  .describe("OpencodePatchPart");

export const opencodeAgentPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("agent"),
    name: z.string(),
    source: opencodeFilePartSourceTextSchema.optional(),
  })
  .passthrough()
  .describe("OpencodeAgentPart");

export const opencodeRetryPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("retry"),
    attempt: z.number(),
    error: opencodeApiErrorSchema,
    time: z.object({
      created: z.number(),
    }),
  })
  .passthrough()
  .describe("OpencodeRetryPart");

export const opencodeCompactionPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("compaction"),
    auto: z.boolean(),
  })
  .passthrough()
  .describe("OpencodeCompactionPart");

export const opencodePartDataSchema = z
  .discriminatedUnion("type", [
    opencodeTextPartSchema,
    opencodeSubtaskPartSchema,
    opencodeReasoningPartSchema,
    opencodeFilePartSchema,
    opencodeToolPartSchema,
    opencodeStepStartPartSchema,
    opencodeStepFinishPartSchema,
    opencodeSnapshotPartSchema,
    opencodePatchPartSchema,
    opencodeAgentPartSchema,
    opencodeRetryPartSchema,
    opencodeCompactionPartSchema,
  ])
  .describe("OpencodePartData");

export const opencodeMessagePartSchema = z
  .object({
    id: z.uuid(),
    opencodeMessageUuid: z.uuid(),
    opencodeId: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
    partData: opencodePartDataSchema,
  })
  .describe("OpencodeMessagePart");

export const sessionMessageWithPartsSchema = z
  .object({
    message: opencodeMessageSchema,
    parts: z.array(opencodeMessagePartSchema),
  })
  .describe("SessionMessageWithParts");

export const opencodeSessionWithRecentMessagesSchema = opencodeSessionSchema
  .extend({
    recentMessages: z.array(sessionMessageWithPartsSchema),
    /**
     * Server-derived sort key for the sidebar: the most recent user-message
     * `createdAt` for this session, falling back to `updatedAt` when the
     * session has no user messages yet. The frontend BOTH sorts and groups
     * by this — sorting by one field and grouping by another would
     * re-introduce duplicate "Today" / "Yesterday" headings when the two
     * disagree.
     */
    lastUserMessageAt: z.coerce.date(),
    /**
     * Did assistant activity happen after the user last read this session?
     * Computed server-side by comparing the latest assistant-message activity
     * key against the read watermark (same column, same clock — see
     * `OpencodeSession.readAt`). The dot uses this for "finished and
     * unread"; correct on first paint, no websocket required.
     */
    isUnread: z.boolean(),
    // Pinned to the top of the sidebar; derived server-side from `OpencodeSession.pinnedAt`.
    isPinned: z.boolean(),
    // Archived into the collapsed group; takes precedence over pinned. Derived from `archivedAt`.
    isArchived: z.boolean(),
  })
  .describe("OpencodeSessionWithRecentMessages");

// Which sidebar partition to return: `active` (excludes pinned/archived) or `archived` (footer group).
export const opencodeSessionListFilterSchema = z
  .enum(["active", "archived"])
  .describe("OpencodeSessionListFilter");

// ------------------------------------------------------------
// Tool input schemas
// ------------------------------------------------------------

export const opencodeTodoItemStatusSchema = z
  .enum(["pending", "in_progress", "completed", "cancelled"])
  .describe("OpencodeTodoItemStatus");

export const opencodeTodowriteToolTodoSchema = z
  .object({
    content: z.string(),
    status: opencodeTodoItemStatusSchema,
    activeForm: z.string().optional(),
    priority: z.string().optional(),
  })
  .describe("OpencodeTodowriteToolTodo");

const opencodeToolOutputFileAttachmentSchema = z.object({
  type: z.literal("file"),
  mime: z.string(),
  url: z.string(),
});

const opencodeToolOutputFileDiffSchema = z.object({
  file: z.string(),
  before: z.string(),
  after: z.string(),
  additions: z.number(),
  deletions: z.number(),
});

const opencodeToolOutputTodoSchema = z
  .object({
    content: z.string().optional(),
    priority: z.string().optional(),
    status: z.string().optional(),
  })
  .passthrough();

function opencodeToolOutputSchema(metadata: z.ZodType) {
  return z.object({
    title: z.string(),
    output: z.string(),
    metadata,
    attachments: z.array(opencodeToolOutputFileAttachmentSchema).optional(),
  });
}

export const opencodeBashToolInputSchema = z.object({
  command: z.string(),
  description: z.string().optional(),
  timeout: z.number().optional(),
  workdir: z.string().optional(),
});

export const opencodeEditToolInputSchema = z.object({
  filePath: z.string(),
  oldString: z.string(),
  newString: z.string(),
  replaceAll: z.boolean().optional(),
});

export const opencodeGlobToolInputSchema = z.object({
  pattern: z.string(),
  path: z.string().optional(),
});

export const opencodeGrepToolInputSchema = z.object({
  pattern: z.string(),
  path: z.string().optional(),
  include: z.string().optional(),
});

export const opencodeQuestionSchema = z
  .object({
    id: z.string().optional(),
    prompt: z.string().optional(),
    question: z.string().optional(),
    header: z.string().optional(),
    multiple: z.boolean().optional(),
    allow_multiple: z.boolean().optional(),
    custom: z.boolean().optional(),
    options: z.array(
      z.object({
        id: z.string().optional(),
        label: z.string(),
        description: z.string().optional(),
        // Honored by the renderer but never arrives: opencode's question tool
        // strips unknown option fields, so agents embed the image as markdown
        // in `description` instead.
        imageUrl: z.string().optional(),
      }),
    ),
  })
  .describe("OpencodeQuestion");

export const opencodeQuestionToolInputSchema = z.object({
  questions: z.array(opencodeQuestionSchema),
});

export const opencodeExecuteToolInputSchema = z
  .object({
    code: z.string(),
  })
  .describe("OpencodeExecuteToolInput");

export const opencodeExecuteToolCallSchema = z
  .object({
    tool: z.string(),
    status: z.enum(["running", "completed", "error"]),
    input: z.record(z.string(), z.unknown()).optional(),
  })
  .describe("OpencodeExecuteToolCall");

export const opencodeExecuteToolMetadataSchema = z
  .object({
    toolCalls: z.array(opencodeExecuteToolCallSchema).default([]),
  })
  .describe("OpencodeExecuteToolMetadata");

export const opencodeReadToolInputSchema = z.object({
  filePath: z.string(),
  offset: z.number().optional(),
  limit: z.number().optional(),
});

export const opencodeSkillToolInputSchema = z.object({
  name: z.string(),
});

export const opencodeTaskToolInputSchema = z.object({
  description: z.string(),
  prompt: z.string(),
  subagent_type: z.string(),
  task_id: z.string().optional(),
  command: z.string().optional(),
});

export const opencodeTodowriteToolInputSchema = z
  .object({
    todos: z.array(opencodeTodowriteToolTodoSchema),
  })
  .describe("OpencodeTodowriteToolInput");

export const opencodeWebfetchToolInputSchema = z.object({
  url: z.string(),
  format: z.enum(["text", "markdown", "html"]).optional(),
  timeout: z.number().optional(),
  prompt: z.string().optional(),
  extract_main: z.boolean().optional(),
  prefer_llms_txt: z.enum(["auto", "always", "never"]).optional(),
  include_metadata: z.boolean().optional(),
  save_binary: z.boolean().optional(),
});

export const opencodeWriteToolInputSchema = z.object({
  filePath: z.string(),
  content: z.string(),
});

export const opencodeBashToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    output: z.string(),
    exit: z.number().nullable(),
    description: z.string(),
  }),
);

export const opencodeEditToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    diagnostics: z.record(z.string(), z.unknown()),
    diff: z.string(),
    filediff: opencodeToolOutputFileDiffSchema,
  }),
);

export const opencodeGlobToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    count: z.number(),
    truncated: z.boolean(),
  }),
);

export const opencodeGrepToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    matches: z.number(),
    truncated: z.boolean(),
  }),
);

export const opencodeQuestionToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    answers: z.array(
      z
        .object({
          question: z.string().optional(),
          answer: z.union([z.string(), z.array(z.string())]).optional(),
          answers: z.array(z.string()).optional(),
        })
        .passthrough(),
    ),
  }),
);

export const opencodeReadToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    preview: z.string(),
    truncated: z.boolean(),
    loaded: z.array(z.string()),
  }),
);

export const opencodeSkillToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    name: z.string(),
    dir: z.string(),
  }),
);

export const opencodeTaskToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    sessionId: z.string(),
    model: z.object({
      modelID: z.string(),
      providerID: z.string(),
    }),
  }),
);

export const opencodeTodowriteToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    todos: z.array(opencodeToolOutputTodoSchema),
  }),
);

export const opencodeWebfetchToolOutputSchema = opencodeToolOutputSchema(
  z.object({}),
);

export const opencodeWriteToolOutputSchema = opencodeToolOutputSchema(
  z.object({
    diagnostics: z.record(z.string(), z.unknown()),
    filepath: z.string(),
    exists: z.boolean(),
  }),
);

export const opencodeUnknownToolOutputSchema = opencodeToolOutputSchema(
  z.record(z.string(), z.unknown()),
).describe("OpencodeUnknownToolOutput");

// ------------------------------------------------------------
// Upstream schemas
// ------------------------------------------------------------

// NOTE (Max, 2026-02-10): 'upstream' means that the data is coming directly from Opencode
// and is not yet transformed into our internal representation.

export const opencodeUpstreamMessage = z
  .object({
    id: z.string().min(1),
    sessionID: z.string().min(1),
    role: z.enum(["user", "assistant"]),
    time: z.object({
      created: z.number(),
      completed: z.number().optional(),
    }),
    parentID: z.string().min(1).optional(),
    finish: z.string().min(1).optional(),
    // opencode reports the chosen model on the message-level payload as a
    // provider/model pair (e.g. `anthropic` + `claude-sonnet-5`), alongside
    // `tokens`/`cost`. It is NOT on step-start parts.
    providerID: z.string().min(1).optional(),
    modelID: z.string().min(1).optional(),
    cost: z.number().optional(),
    tokens: z
      .object({
        input: z.number(),
        output: z.number(),
        reasoning: z.number().optional(),
        cache: z
          .object({
            read: z.number().optional(),
            write: z.number().optional(),
          })
          .optional(),
      })
      .optional(),
    error: z.unknown().optional(),
  })
  .passthrough()
  .describe("OpencodeUpstreamMessage");

export const opencodeUpstreamPart = z
  .object({
    id: z.string().min(1),
    messageID: z.string().min(1),
    sessionID: z.string().min(1),
    type: z.string().optional(),
  })
  .passthrough()
  .describe("OpencodeUpstreamPart");

// One entry of opencode's `GET /session/:id/message` response.
export const opencodeUpstreamSessionMessage = z.object({
  info: opencodeUpstreamMessage,
  parts: z.array(opencodeUpstreamPart),
});

export const messageUpdatedEventSchema = z.object({
  type: z.literal("message.updated"),
  properties: z.object({
    info: opencodeUpstreamMessage,
  }),
});

export const messageCreatedEventSchema = z.object({
  type: z.literal("message.created"),
  properties: z.object({
    info: opencodeUpstreamMessage,
  }),
});

export const messagePartUpdatedEventSchema = z.object({
  type: z.literal("message.part.updated"),
  properties: z.object({
    part: opencodeUpstreamPart,
  }),
});

export const messagePartAddedEventSchema = z.object({
  type: z.literal("message.part.added"),
  properties: z.object({
    part: opencodeUpstreamPart,
  }),
});

export const messagePartCreatedEventSchema = z.object({
  type: z.literal("message.part.created"),
  properties: z.object({
    part: opencodeUpstreamPart,
  }),
});

export const messagePartDeltaEventSchema = z
  .object({
    type: z.literal("message.part.delta"),
    properties: z.object({
      sessionID: z.string(),
      messageID: z.string(),
      partID: z.string(),
      field: z.string(),
      delta: z.string(),
    }),
  })
  .describe("MessagePartDeltaEvent");

// ---------------------------------------------------------------------------
// Non-message event schemas
// ---------------------------------------------------------------------------

// -- Session lifecycle events -----------------------------------------------

const opencodeUpstreamSession = z
  .object({
    id: z.string(),
    projectID: z.string().optional(),
    directory: z.string().optional(),
    title: z.string().optional(),
    version: z.string().optional(),
    parentID: z.string().optional(),
    time: z
      .object({
        created: z.number(),
        updated: z.number(),
        compacting: z.number().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const sessionStatusValueSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("idle") }),
  z.object({ type: z.literal("busy") }),
  z.object({
    type: z.literal("retry"),
    attempt: z.number(),
    message: z.string(),
    next: z.number(),
  }),
]);

const sessionStatusEventSchema = z.object({
  type: z.literal("session.status"),
  properties: z.object({
    sessionID: z.string(),
    status: sessionStatusValueSchema,
  }),
});

const sessionIdleEventSchema = z.object({
  type: z.literal("session.idle"),
  properties: z.object({
    sessionID: z.string(),
  }),
});

const sessionCompactedEventSchema = z.object({
  type: z.literal("session.compacted"),
  properties: z.object({
    sessionID: z.string(),
  }),
});

export const sessionCreatedEventSchema = z
  .object({
    type: z.literal("session.created"),
    properties: z.object({
      info: opencodeUpstreamSession,
    }),
  })
  .describe("SessionCreatedEvent");

export const sessionUpdatedEventSchema = z.object({
  type: z.literal("session.updated"),
  properties: z.object({
    info: opencodeUpstreamSession,
  }),
});

const sessionDeletedEventSchema = z.object({
  type: z.literal("session.deleted"),
  properties: z.object({
    info: opencodeUpstreamSession,
  }),
});

const sessionDiffEventSchema = z.object({
  type: z.literal("session.diff"),
  properties: z.object({
    sessionID: z.string(),
    diff: z.array(
      z.object({
        file: z.string(),
        before: z.string(),
        after: z.string(),
        additions: z.number(),
        deletions: z.number(),
      }),
    ),
  }),
});

const sessionErrorEventSchema = z.object({
  type: z.literal("session.error"),
  properties: z.object({
    sessionID: z.string().optional(),
    error: z.unknown().optional(),
  }),
});

// -- Permission events ------------------------------------------------------

export const permissionAskedEventSchema = z.object({
  type: z.literal("permission.asked"),
  properties: opencodePermissionRequestSchema,
});

export const permissionRepliedEventSchema = z.object({
  type: z.literal("permission.replied"),
  properties: z.object({
    sessionID: z.string(),
    requestID: z.string(),
    reply: z.enum(["once", "always", "reject"]),
  }),
});

export const opencodePersistenceEventSchema = z
  .discriminatedUnion("type", [
    messageUpdatedEventSchema,
    messageCreatedEventSchema,
    messagePartUpdatedEventSchema,
    messagePartAddedEventSchema,
    messagePartCreatedEventSchema,
    sessionCreatedEventSchema,
    sessionUpdatedEventSchema,
    sessionDeletedEventSchema,
    sessionErrorEventSchema,
    permissionAskedEventSchema,
    permissionRepliedEventSchema,
  ])
  .describe("OpencodePersistenceEvent");

// -- Question events --------------------------------------------------------

export const questionAskedEventSchema = z.object({
  type: z.literal("question.asked"),
  properties: z
    .object({
      id: z.string(),
      questions: opencodeQuestionToolInputSchema.shape.questions,
      sessionID: z.string(),
      tool: z.object({ callID: z.string() }).passthrough(),
    })
    .passthrough(),
});

export const opencodeQuestionAnswersSchema = z.array(z.array(z.string()));

export function findQuestionAnswers({
  event,
}: {
  event: OpencodeEvent;
}): string[][] | null {
  if (event.type !== "message.part.updated") {
    return null;
  }
  const part = opencodeToolPartSchema.safeParse(event.properties.part);
  if (
    !part.success ||
    part.data.tool.toLowerCase() !== "question" ||
    part.data.state.status !== "completed"
  ) {
    return null;
  }
  const answers = opencodeQuestionAnswersSchema.safeParse(
    part.data.state.metadata.answers,
  );
  return answers.success ? answers.data : null;
}

// -- Other events -----------------------------------------------------------

const todoUpdatedEventSchema = z.object({
  type: z.literal("todo.updated"),
  properties: z.object({
    sessionID: z.string(),
    todos: z.array(
      z.object({
        id: z.string(),
        content: z.string(),
        status: z.string(),
        priority: z.string(),
      }),
    ),
  }),
});

const fileEditedEventSchema = z.object({
  type: z.literal("file.edited"),
  properties: z.object({
    file: z.string(),
  }),
});

// ---------------------------------------------------------------------------
// Unified opencode event schema
// ---------------------------------------------------------------------------

const opencodeSessionEventSchemas = [
  sessionStatusEventSchema,
  sessionIdleEventSchema,
  sessionCompactedEventSchema,
  sessionCreatedEventSchema,
  sessionUpdatedEventSchema,
  sessionDeletedEventSchema,
  sessionDiffEventSchema,
  sessionErrorEventSchema,
] as const;

export const opencodeSessionEventSchema = z
  .discriminatedUnion("type", opencodeSessionEventSchemas)
  .describe("OpencodeSessionEvent");

export const opencodeEventSchema = z.discriminatedUnion("type", [
  // Persisted message events
  messageUpdatedEventSchema,
  messageCreatedEventSchema,
  messagePartUpdatedEventSchema,
  messagePartAddedEventSchema,
  messagePartCreatedEventSchema,
  messagePartDeltaEventSchema,
  // Session lifecycle events
  ...opencodeSessionEventSchemas,
  // Permission events
  permissionAskedEventSchema,
  permissionRepliedEventSchema,
  // Question events
  questionAskedEventSchema,
  // Other events
  todoUpdatedEventSchema,
  fileEditedEventSchema,
]);

export type OpencodeEvent = z.infer<typeof opencodeEventSchema>;

// ---------------------------------------------------------------------------
// Persisted event subset — events that write OpenCode state to storage.
// ---------------------------------------------------------------------------

export function isPersistenceEvent(
  event: OpencodeEvent,
): event is z.infer<typeof opencodePersistenceEventSchema> {
  return opencodePersistenceEventSchema.safeParse(event).success;
}

export function getOpencodeSessionIdFromOpencodeEvent(
  event: OpencodeEvent,
): string | undefined {
  return exhaustiveSwitch(event)({
    "message.updated": (event) => event.properties.info.sessionID,
    "message.created": (event) => event.properties.info.sessionID,
    "message.part.updated": (event) => event.properties.part.sessionID,
    "message.part.added": (event) => event.properties.part.sessionID,
    "message.part.created": (event) => event.properties.part.sessionID,
    "message.part.delta": (event) => event.properties.sessionID,
    "session.status": (event) => event.properties.sessionID,
    "session.idle": (event) => event.properties.sessionID,
    "session.compacted": (event) => event.properties.sessionID,
    "session.created": (event) => event.properties.info.id,
    "session.updated": (event) => event.properties.info.id,
    "session.deleted": (event) => event.properties.info.id,
    "session.diff": (event) => event.properties.sessionID,
    "session.error": (event) => event.properties.sessionID,
    "permission.asked": (event) => event.properties.sessionID,
    "permission.replied": (event) => event.properties.sessionID,
    "question.asked": (event) => event.properties.sessionID,
    "todo.updated": (event) => event.properties.sessionID,
    "file.edited": () => undefined,
  });
}
